import { useState, useCallback, useMemo } from 'react';
import { useOrgContext } from '@/hooks/useOrgContext';
import {
  useVoicePhoneNumbers,
  useVoiceSipTrunks,
  useVoiceAgentsList,
  useVoicePhoneNumberMutations,
} from '@/hooks/useVoiceAgents';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Phone } from 'lucide-react';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up

export function VoicePhoneNumbersPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { data: phoneNumbers, isLoading: numbersLoading } = useVoicePhoneNumbers(currentOrg?._id);
  const { data: sipTrunks } = useVoiceSipTrunks(currentOrg?._id);
  const { data: agents } = useVoiceAgentsList(currentOrg?._id);
  const { create, remove } = useVoicePhoneNumberMutations();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Id<'voicePhoneNumbers'> | null>(null);

  const isLoading = orgLoading || numbersLoading;
  const numbers = phoneNumbers ?? [];
  const activeTrunks = useMemo(() => (sipTrunks ?? []).filter(t => t.isActive), [sipTrunks]);

  const trunkMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of sipTrunks ?? []) {
      map.set(t._id, t.name);
    }
    return map;
  }, [sipTrunks]);

  const agentMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agents ?? []) {
      if (a.phoneNumberId) {
        map.set(a.phoneNumberId, a.name);
      }
    }
    return map;
  }, [agents]);

  const handleCreate = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!currentOrg) return;
      const formData = new FormData(e.currentTarget);
      await create({
        orgId: currentOrg._id,
        phoneNumber: formData.get('phoneNumber') as string, // AS_JUSTIFIED: FormData.get returns FormDataEntryValue
        label: formData.get('label') as string, // AS_JUSTIFIED: FormData.get returns FormDataEntryValue
        sipTrunkId: formData.get('sipTrunkId') as Id<'voiceSipTrunks'>, // AS_JUSTIFIED: Select enforces valid Id value
      });
      setShowAddDialog(false);
    },
    [currentOrg, create]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await remove({ id: deleteTarget });
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Phone className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage phone numbers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Phone Numbers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? 'Loading...'
              : `${numbers.length} number${numbers.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
          Add Number
        </Button>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Phone Number</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>SIP Trunk</TableHead>
              <TableHead>Assigned Agent</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                  Loading phone numbers...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && numbers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-16">
                  <Phone className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No phone numbers configured yet</p>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              numbers.map(num => (
                <TableRow key={num._id}>
                  <TableCell className="font-mono font-medium">{num.phoneNumber}</TableCell>
                  <TableCell>{num.label}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {trunkMap.get(num.sipTrunkId) ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {agentMap.get(num._id) ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={num.isActive ? 'default' : 'secondary'}>
                      {num.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(num._id)}
                      title="Delete number"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
            <DialogDescription>
              Add a phone number in E.164 format and assign it to a SIP trunk.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone-number">Phone Number (E.164)</Label>
              <Input
                id="phone-number"
                name="phoneNumber"
                placeholder="+14155551234"
                pattern="^\+[1-9]\d{1,14}$"
                title="Enter a valid E.164 phone number (e.g. +14155551234)"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone-label">Label</Label>
              <Input id="phone-label" name="label" placeholder="Main Line" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone-sip-trunk">SIP Trunk</Label>
              <Select name="sipTrunkId" required>
                <SelectTrigger id="phone-sip-trunk" className="w-full">
                  <SelectValue placeholder="Select SIP trunk" />
                </SelectTrigger>
                <SelectContent>
                  {activeTrunks.map(trunk => (
                    <SelectItem key={trunk._id} value={trunk._id}>
                      {trunk.name} ({trunk.provider})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeTrunks.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No active SIP trunks available. Create one first.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={activeTrunks.length === 0}>
                Add Number
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Phone Number?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the phone number. Any agent assigned to this number will
              lose its phone connectivity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
